
import React, { useState } from 'react';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { BusinessFormData } from './types';
import { usStates } from '@/utils/stateOptions';
import { timezoneOptions } from '@/utils/timezoneOptions';

interface LocationTabProps {
  formData: BusinessFormData;
  handleInputChange: (e: React.ChangeEvent<HTMLInputElement>) => void;
  handleSelectChange: (value: string, field: string) => void;
}

export const LocationTab: React.FC<LocationTabProps> = ({
  formData,
  handleInputChange,
  handleSelectChange
}) => {
  const [timezoneManuallySelected, setTimezoneManuallySelected] = useState(false);

  // Handle timezone selection - mark as manually selected
  const handleTimezoneChange = (value: string) => {
    setTimezoneManuallySelected(true);
    handleSelectChange(value, 'timeZone');
  };

  // Handle state change with timezone auto-set
  const handleStateChange = (value: string) => {
    handleSelectChange(value, 'stateCode');
    
    // Only set default timezone if it hasn't been manually selected
    if (!timezoneManuallySelected) {
      let defaultTimezone = '';
      
      // Map state to default timezone
      switch(value) {
        case 'ME': case 'NH': case 'VT': case 'MA': case 'RI': case 'CT': 
        case 'NY': case 'NJ': case 'DE': case 'MD': case 'DC': case 'VA': 
        case 'NC': case 'SC': case 'GA': case 'FL': case 'PA': case 'WV':
          defaultTimezone = 'America/New_York'; // Eastern
          break;
        case 'OH': case 'MI': case 'IN': case 'KY': case 'TN': case 'AL': 
        case 'MS': case 'IL': case 'MO': case 'AR': case 'LA': case 'WI': 
        case 'MN': case 'IA':
          defaultTimezone = 'America/Chicago'; // Central
          break;
        case 'ND': case 'SD': case 'NE': case 'KS': case 'OK': 
        case 'TX': case 'MT': case 'WY': case 'CO': case 'NM':
          defaultTimezone = 'America/Denver'; // Mountain
          break;
        case 'AZ':
          defaultTimezone = 'America/Phoenix'; // Arizona (no DST)
          break;
        case 'ID': case 'UT': case 'NV': case 'CA': case 'OR': case 'WA':
          defaultTimezone = 'America/Los_Angeles'; // Pacific
          break;
        case 'AK':
          defaultTimezone = 'America/Anchorage'; // Alaska
          break;
        case 'HI':
          defaultTimezone = 'Pacific/Honolulu'; // Hawaii
          break;
        default:
          defaultTimezone = 'America/New_York'; // Default
      }
      
      // Set the timezone directly
      handleSelectChange(defaultTimezone, 'timeZone');
    }
  };

  return (
    <div className="space-y-4">
      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        <div className="space-y-2 col-span-1 md:col-span-2">
          <Label htmlFor="address">Street Address</Label>
          <Input 
            id="address" 
            name="address" 
            value={formData.address} 
            onChange={handleInputChange} 
            placeholder="123 Main St" 
          />
        </div>
        
        <div className="space-y-2">
          <Label htmlFor="cityName">City</Label>
          <Input 
            id="cityName" 
            name="cityName" 
            value={formData.cityName} 
            onChange={handleInputChange} 
            placeholder="City" 
          />
        </div>
        
        <div className="space-y-2">
          <Label htmlFor="stateCode">State</Label>
          <Select 
            value={formData.stateCode} 
            onValueChange={handleStateChange}
          >
            <SelectTrigger id="stateCode">
              <SelectValue placeholder="State" />
            </SelectTrigger>
            <SelectContent>
              {usStates.map((state) => (
                <SelectItem key={state.value} value={state.value}>
                  {state.label}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>
        
        <div className="space-y-2">
          <Label htmlFor="postalCode">ZIP Code</Label>
          <Input 
            id="postalCode" 
            name="postalCode" 
            value={formData.postalCode} 
            onChange={handleInputChange} 
            placeholder="12345" 
          />
        </div>
        
        <div className="space-y-2">
          <Label htmlFor="timeZone">Time Zone</Label>
          <Select 
            value={formData.timeZone} 
            onValueChange={handleTimezoneChange}
          >
            <SelectTrigger id="timeZone">
              <SelectValue placeholder="Select time zone" />
            </SelectTrigger>
            <SelectContent>
              {timezoneOptions.map((tz) => (
                <SelectItem key={tz.value} value={tz.value}>
                  {tz.label}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>
      </div>
    </div>
  );
};
